/**
 * What an `@Img` field holds in the JSON clob: a reference, and the words that go with
 * it at THIS place on the page.
 *
 * Only the path, because a file is uploaded once and never renamed. Everything else
 * about it — original name, size, type, when — lives in the `images` table and is looked
 * up by path. Copying that record into every document is how a rename, a re-upload or a
 * reordered list makes two of them disagree.
 *
 * `alt` is not in the table on purpose: it describes the use, not the file. The same
 * photograph on two pages wants two different sentences.
 */
export interface ImageValue {
  /** Public URL path, and the reference the `images` table is keyed by. */
  path: string;
  /** Alt text. Empty is allowed but never undefined, so the field always renders. */
  alt: string;
}

/** One row of the `images` table — what the editor is shown ABOUT a file. */
export interface ImageRecord {
  path: string;
  originalName: string;
  /** Normalised extension, derived from the MIME type — not from the filename. */
  ext: string;
  mime: string;
  bytes: number;
  uploadedAt: number;
}

export const EMPTY_IMAGE: ImageValue = { path: '', alt: '' };

export const hasImage = (v: ImageValue | undefined | null): v is ImageValue =>
  Boolean(v && v.path);

/**
 * Where the bytes sit in the checkout — `public/img/3f9c….jpg`.
 *
 * The editor needs this and not the URL: uploading on the server writes into the
 * checkout, so the file is lost on the next deploy unless it is committed.
 */
export const publicPath = (v: ImageValue): string => (v.path ? `public${v.path}` : '');
